import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactForm() {
    const [form, setForm] = useState({
        name: "",
        email: "",
        company: "",
        role: "",
    });
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);

    const handle = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email || !form.company || !form.role) {
            toast.error("Please fill in every field.");
            return;
        }
        setLoading(true);
        try {
            const tier =
                (typeof window !== "undefined" &&
                    sessionStorage.getItem("selected_tier")) ||
                null;
            const res = await axios.post(`${API}/leads`, { ...form, tier });
            setDone(true);
            toast.success("Booking your slot — redirecting to Calendly…");
            const url = res.data?.calendly_url;
            setTimeout(() => {
                if (url) window.location.href = url;
            }, 1400);
        } catch (err) {
            console.error(err);
            toast.error(
                err?.response?.data?.detail ||
                    "Something went wrong. Try again.",
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <section
            id="contact"
            data-testid="contact-section"
            className="rule-top bg-[#050505]"
        >
            <div className="container-x py-24 md:py-40 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8 }}
                    className="lg:col-span-6"
                >
                    <span className="tag">Book a Strategy Call</span>
                    <h2 className="font-display uppercase text-white text-6xl md:text-8xl mt-4 leading-[0.88]">
                        Thirty
                        <br />
                        <span className="font-editorial italic font-normal normal-case text-[#e84125] tracking-normal">
                            meetings
                        </span>
                        <br />
                        in thirty
                        <br />
                        days.
                        <span className="caret" aria-hidden />
                    </h2>
                    <p className="font-body text-white/60 text-lg mt-8 max-w-[46ch]">
                        Twenty-minute call. We&apos;ll map your current
                        pipeline gaps, walk the dual-engine architecture on
                        your stack, and if it&apos;s a fit — hand you a 90-day
                        plan.
                    </p>
                    <div className="mt-10 font-mono uppercase text-[0.7rem] tracking-[0.22em] text-white/40 space-y-2">
                        <div>
                            hello@viraldigitallabs.in
                            <span className="text-white/20"> ·</span>
                        </div>
                        <div>
                            +91 98181 16918
                            <span className="text-white/20"> ·</span>
                        </div>
                        <div>New Delhi · Serving worldwide</div>
                    </div>
                </motion.div>

                <motion.form
                    onSubmit={submit}
                    noValidate
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8, delay: 0.15 }}
                    data-testid="lead-form"
                    className="lg:col-span-6 lg:pl-12 flex flex-col gap-8"
                >
                    <div>
                        <label className="font-mono uppercase text-[0.62rem] tracking-[0.28em] text-white/50">
                            01 · Name
                        </label>
                        <input
                            className="field mt-2"
                            placeholder="Ada Lovelace"
                            value={form.name}
                            onChange={handle("name")}
                            data-testid="lead-input-name"
                        />
                    </div>
                    <div>
                        <label className="font-mono uppercase text-[0.62rem] tracking-[0.28em] text-white/50">
                            02 · Email
                        </label>
                        <input
                            type="email"
                            className="field mt-2"
                            placeholder="ada@yourco.com"
                            value={form.email}
                            onChange={handle("email")}
                            data-testid="lead-input-email"
                        />
                    </div>
                    <div>
                        <label className="font-mono uppercase text-[0.62rem] tracking-[0.28em] text-white/50">
                            03 · Company
                        </label>
                        <input
                            className="field mt-2"
                            placeholder="YourCo Payment Automation"
                            value={form.company}
                            onChange={handle("company")}
                            data-testid="lead-input-company"
                        />
                    </div>
                    <div>
                        <label className="font-mono uppercase text-[0.62rem] tracking-[0.28em] text-white/50">
                            04 · Role
                        </label>
                        <input
                            className="field mt-2"
                            placeholder="VP Sales · CFO · Founder"
                            value={form.role}
                            onChange={handle("role")}
                            data-testid="lead-input-role"
                        />
                    </div>
                    <div className="pt-4">
                        <button
                            type="submit"
                            disabled={loading || done}
                            data-testid="lead-submit"
                            className="btn-primary w-full sm:w-auto disabled:opacity-60"
                        >
                            {done
                                ? "Redirecting…"
                                : loading
                                  ? "Sending…"
                                  : "Book my strategy call →"}
                        </button>
                        <p className="font-mono uppercase text-[0.6rem] tracking-[0.22em] text-white/40 mt-4">
                            After submit, you&apos;ll be routed to our
                            Calendly to pick a time.
                        </p>
                    </div>
                </motion.form>
            </div>
        </section>
    );
}
